import { ActionTypeKeys as CommonActionTypeKeys, ActionTypes as CommonActionTypes } from 'common/actions'
import { RateTable } from 'embed/constants'
import { CreditOffer, DepositsOffer, LifeInsuranceOffer, LoanOffer, Offer } from 'embed/constants/models'
import { sortOffers } from 'embed/utils/sort-offers'
import {
  CreditCardStep,
  CreditSortOptionUI,
  DepositsSortOption,
  LifeInsuranceSortOption,
  LoanSortOption,
  LoanStep,
  Product,
  ProductResponseKeys,
  SortDirection,
  SortOption,
} from 'even-ts-static'
import ratesFiltersReducer, { ratesFiltersInitialState, RatesFiltersState } from 'embed/reducers/ratesFilter'

export type MaxResultsState = { [key in Product]?: number }

// Selected represets a "selected" offer that typically comes from a query parameter
export type SelectedState = {
  [Product.CreditCard]?: CreditOffer
  [Product.Deposits]?: DepositsOffer
  [Product.Loan]?: LoanOffer
  [Product.LifeInsurance]?: LifeInsuranceOffer
} & {
  loadOfferError?: { [key in Product]?: boolean }
}

export type SortedState = {
  [key in Product]?: {
    // TODO: improve type
    [key: string]: unknown[]
  }
}

export type RateTableWithRequestTimestamp = RateTable & { requestTimestamp?: number }

export type RatesState = {
  filters: RatesFiltersState
  maxResults: MaxResultsState
  selected: SelectedState
  sortDirection: {
    [Product.Deposits]: SortDirection
    [Product.LifeInsurance]: SortDirection
    [Product.Loan]: SortDirection
    [Product.CreditCard]: SortDirection
  }
  sorted: SortedState
  sortOption: {
    [Product.Deposits]: DepositsSortOption
    [Product.LifeInsurance]: LifeInsuranceSortOption
    [Product.Loan]: LoanSortOption
    [Product.CreditCard]: CreditSortOptionUI
  }
} & {
  [key in Product]: RateTableWithRequestTimestamp
}

export const InitialSortDirection = {
  [Product.LifeInsurance]: SortDirection.Desc,
  [Product.Loan]: SortDirection.Asc,
  [Product.Deposits]: SortDirection.Desc,
  [Product.CreditCard]: SortDirection.Desc,
}

export const InitialSortOption = {
  [Product.LifeInsurance]: LifeInsuranceSortOption.Recommended,
  [Product.Loan]: LoanSortOption.Apr,
  [Product.Deposits]: DepositsSortOption.Recommended,
  [Product.CreditCard]: CreditSortOptionUI.Recommended,
}

export const initialRatesState: RatesState = {
  filters: ratesFiltersInitialState,
  maxResults: {
    [Product.Deposits]: 3,
    [Product.LifeInsurance]: 15,
  },
  selected: {
    loadOfferError: {},
  },
  sortDirection: InitialSortDirection,
  sorted: {},
  sortOption: InitialSortOption,
  [Product.CreditCard]: {
    creditCardOffers: [],
    leadUuid: '',
    lifeInsuranceOffers: [],
    loanOffers: [],
    mortgageOffers: [],
    savingsOffers: [],
    specialOffers: [],
    uuid: '',
  },
  [Product.Deposits]: {
    creditCardOffers: [],
    leadUuid: '',
    lifeInsuranceOffers: [],
    loanOffers: [],
    mortgageOffers: [],
    savingsOffers: [],
    specialOffers: [],
    uuid: '',
  },
  [Product.LifeInsurance]: {
    creditCardOffers: [],
    leadUuid: '',
    lifeInsuranceOffers: [],
    loanOffers: [],
    mortgageOffers: [],
    savingsOffers: [],
    specialOffers: [],
    uuid: '',
  },
  [Product.Loan]: {
    creditCardOffers: [],
    leadUuid: '',
    lifeInsuranceOffers: [],
    loanOffers: [],
    mortgageOffers: [],
    savingsOffers: [],
    specialOffers: [],
    uuid: '',
  },
  [Product.Mortgage]: {
    creditCardOffers: [],
    leadUuid: '',
    lifeInsuranceOffers: [],
    loanOffers: [],
    mortgageOffers: [],
    savingsOffers: [],
    specialOffers: [],
    uuid: '',
  },
  [Product.Other]: {
    creditCardOffers: [],
    leadUuid: '',
    lifeInsuranceOffers: [],
    loanOffers: [],
    mortgageOffers: [],
    savingsOffers: [],
    specialOffers: [],
    uuid: '',
  },
}

const sortedReducer = (
  sorted: SortedState = initialRatesState.sorted,
  action: CommonActionTypes,
  offers: Offer[],
  sort: SortOption,
  direction: SortDirection,
): SortedState => {
  switch (action.type) {
    case CommonActionTypeKeys.SORT_CHANGED:
    case CommonActionTypeKeys.FETCH_RATE_TABLES_SUCCESS:
    case CommonActionTypeKeys.FETCH_PREVIEW_OFFERS_SUCCESS:
    case CommonActionTypeKeys.STEP_SELECTED:
      const sortedOffers = sortOffers(action.product, offers, sort, direction)
      return {
        ...sorted,
        [action.product]: {
          ...sorted[action.product],
          [`${ProductResponseKeys[action.product]}Offers`]: sortedOffers,
        },
      }
    default:
      return sorted
  }
}

// eslint-disable-next-line complexity
const reducer = (state: RatesState = initialRatesState, action: CommonActionTypes): RatesState => {
  switch (action.type) {
    case CommonActionTypeKeys.STEP_SELECTED:
      if (action.step === CreditCardStep.Preview) {
        return { ...state, selected: initialRatesState.selected }
      } else if (action.step === LoanStep.OfferList && state.sortOption[Product.Loan] === LoanSortOption.Amount) {
        return {
          ...state,
          sorted: sortedReducer(
            state.sorted,
            action,
            state[Product.Loan][`${ProductResponseKeys[Product.Loan]}Offers`],
            LoanSortOption.Recommended,
            state.sortDirection[Product.Loan],
          ),
          sortOption: { ...state.sortOption, [Product.Loan]: LoanSortOption.Recommended },
        }
      }

      return state
    case CommonActionTypeKeys.SET_MAX_RESULTS:
      return {
        ...state,
        maxResults: {
          ...state.maxResults,
          [action.product]: action.max,
          // TODO: We're temporarily overriding the life-insurance maxResults value
          // Remove this once https://evenfinancial.atlassian.net/browse/PORT-1519 is done
          [Product.LifeInsurance]: initialRatesState.maxResults[Product.LifeInsurance],
        },
      }
    case CommonActionTypeKeys.SET_RATE_TABLE_UUID: {
      const nextState = { ...state }
      nextState[action.product] = { ...nextState[action.product], uuid: action.rateTableUuid }
      return nextState
    }
    case CommonActionTypeKeys.SORT_CHANGED:
      return {
        ...state,
        sortDirection: { ...state.sortDirection, [action.product]: action.direction },
        sorted: sortedReducer(
          state.sorted,
          action,
          state[action.product][`${ProductResponseKeys[action.product]}Offers`],
          action.sort,
          action.direction,
        ),
        sortOption: { ...state.sortOption, [action.product]: action.sort },
      }
    case CommonActionTypeKeys.SORT_CLEARED: {
      const nextState = { ...state }
      nextState.sortDirection = InitialSortDirection
      nextState.sortOption = InitialSortOption
      return nextState
    }
    case CommonActionTypeKeys.FETCH_OFFER_ERROR: {
      const nextState = { ...state }
      nextState.selected.loadOfferError = { ...nextState.selected.loadOfferError, [action.product]: true }
      return nextState
    }
    case CommonActionTypeKeys.FETCH_OFFER_SUCCESS: {
      const nextState = { ...state }
      nextState.selected[action.product] = action.data[`${ProductResponseKeys[action.product]}Offer`]
      nextState.selected.loadOfferError = { ...nextState.selected.loadOfferError, [action.product]: false }
      return nextState
    }
    case CommonActionTypeKeys.CREDIT_OFFER_PREVIEW_SELECTED: {
      const nextState = { ...state, selected: { ...state.selected } }
      nextState.selected[Product.CreditCard] = action.offer
      return nextState
    }
    case CommonActionTypeKeys.LIFE_INSURANCE_OFFER_SELECTED: {
      const nextState = { ...state, selected: { ...state.selected } }
      nextState.selected[Product.LifeInsurance] = action.offer
      return nextState
    }
    case CommonActionTypeKeys.LIFE_INSURANCE_OFFER_DESELECTED: {
      const nextState = { ...state, selected: { ...state.selected } }
      nextState.selected[Product.LifeInsurance] = undefined
      return nextState
    }
    case CommonActionTypeKeys.FETCH_RATE_TABLES_SUCCESS:
    case CommonActionTypeKeys.FETCH_PREVIEW_OFFERS_SUCCESS:
    case CommonActionTypeKeys.RATE_TABLE_CHANGED: {
      const nextState = { ...state }

      // ignore stale rateTable responses if arriving out of order
      const stateRequestTimestamp = state[action.product] && state[action.product].requestTimestamp
      if (stateRequestTimestamp && action.data.requestTimestamp < stateRequestTimestamp) {
        return state
      }

      nextState[action.product] = { ...nextState[action.product], ...action.data }
      nextState.sorted = sortedReducer(
        state.sorted,
        action,
        action.data[`${ProductResponseKeys[action.product]}Offers`],
        state.sortOption[action.product],
        state.sortDirection[action.product],
      )
      return nextState
    }
    case CommonActionTypeKeys.REWARD_VALUE_CALCULATION_SUCCESS: {
      const nextState = { ...state, [Product.CreditCard]: { ...state[Product.CreditCard] } }
      const { offerCalculations } = action.data
      nextState[Product.CreditCard][`${ProductResponseKeys[Product.CreditCard]}Offers`] = state[Product.CreditCard][
        `${ProductResponseKeys[Product.CreditCard]}Offers`
      ].map((offer: CreditOffer) => {
        let newCreditOffer = offer
        offerCalculations.forEach((calculation) => {
          if (calculation.staticOfferUuid === offer.uuid) {
            newCreditOffer = { ...offer, details: { ...offer.details } }
            newCreditOffer.details.earningProgram = calculation.earningProgram
          }
        })
        return newCreditOffer
      })
      return nextState
    }
    case CommonActionTypeKeys.REWARD_VALUE_CALCULATION_ERROR: {
      const nextState = { ...state, [Product.CreditCard]: { ...state[Product.CreditCard] } }
      nextState[Product.CreditCard][`${ProductResponseKeys[Product.CreditCard]}Offers`] = state[Product.CreditCard][
        `${ProductResponseKeys[Product.CreditCard]}Offers`
      ].map((offer: CreditOffer) => {
        const { earningProgram, ...restDetails } = offer.details
        return { ...offer, details: { ...restDetails } }
      })
      return nextState
    }
    default:
      return {
        ...state,
        filters: ratesFiltersReducer(state.filters, action),
      }
  }
}

export default reducer
